import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import UserAlertRulesEditor from "../components/UserAlertRulesEditor";
import { useAuth } from "../context/AuthContext";
import { userApiService } from "../services/userApiService";

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5 shadow-soft">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-4xl mt-2">{value}</div>
    </div>
  );
}

export default function AlertsPage() {
  const { user } = useAuth();
  const [activeRules, setActiveRules] = useState("0");
  const [triggeredToday, setTriggeredToday] = useState("0");

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        const [rulesData, notifData] = await Promise.all([
          userApiService.getAlertRules(),
          userApiService.listNotifications({ limit: 200 }),
        ]);
        if (!mounted) return;
        const items = rulesData?.items || [];
        setActiveRules(String(items.filter((r) => r.enabled).length));
        const today = new Date().toISOString().slice(0, 10);
        const count = (notifData?.items || []).filter((n) => {
          const created = n?.createdAt ? new Date(n.createdAt).toISOString().slice(0, 10) : "";
          return created === today;
        }).length;
        setTriggeredToday(String(count));
      } catch {
        if (mounted) setActiveRules("0");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user]);

  return (
    <PageShell>
      <PageHero
        eyebrow="Alerts"
        title="When a reading"
        highlight="crosses a limit."
        description="Turn a rule on, set the number, and pick the device."
      />

      <PageSection variant="content">
        <div className="mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Stat label="Active rules" value={activeRules} />
            <Stat label="Triggered today" value={triggeredToday} />
          </div>

          <UserAlertRulesEditor userId={user?.uid} title="Your rules" />

          <div className="mt-12 rounded-3xl p-8 text-accent-foreground shadow-elevated" style={{ background: "var(--gradient-warm)" }}>
            <AlertTriangle className="h-8 w-8" />
            <h3 className="font-display text-3xl mt-4">How rules work</h3>
            <p className="text-accent-foreground/80 mt-2 max-w-xl">
              A rule watches temperature, humidity, or soil moisture. When a new reading crosses the number you set, the app stores an alert.
            </p>
          </div>
        </div>
      </PageSection>
    </PageShell>
  );
}
